import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Download, Share2, Lock, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { progress, certId, getCertName, setCertName } from "@/lib/progress";

const W = 1123, H = 794;

const Cert = forwardRef(({ name, id, date, labs }, ref) => (
  <div ref={ref} style={{ width: W, height: H }} className="relative bg-[#0A192F] text-white overflow-hidden font-['Outfit']">
    <div className="absolute inset-5 rounded-[28px] border-4 border-[#FFD166]" />
    <div className="absolute inset-9 rounded-[20px] border border-[#4FD1C5]/50" />
    <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#FF2E63]/20" />
    <div className="absolute -left-24 -bottom-32 w-[420px] h-[420px] rounded-full bg-[#4FD1C5]/15" />
    <div className="relative px-20 pt-16 text-center">
      <p className="tracking-[0.4em] text-sm font-bold text-[#4FD1C5]">ROBOSPARK · ROBOT LAB</p>
      <h1 className="mt-4 text-6xl font-black">Certificate of Completion</h1>
      <p className="mt-6 text-lg text-slate-300">This certifies that</p>
      <p className="mt-2 text-5xl font-black text-[#FFD166]" data-testid="cert-name">{name || "Your Name"}</p>
      <p className="mt-5 text-lg text-slate-200 max-w-3xl mx-auto">has successfully completed all 15 interactive Robot Lab projects in drones, robotics, AIoT smart automation and aerospace engineering.</p>
      <div className="mt-6 flex flex-wrap justify-center gap-1.5 max-w-4xl mx-auto">{labs.map((l) => <span key={l.id} className="rounded-full bg-white/10 border border-white/15 px-2.5 py-0.5 text-[11px] font-semibold text-slate-200">{l.title.split(" – ")[0]}</span>)}</div>
    </div>
    <div className="absolute left-20 right-20 bottom-16 flex items-end justify-between text-left">
      <div><p className="text-xs text-slate-400">Certificate ID</p><p className="font-bold text-[#4FD1C5]" data-testid="cert-id">{id}</p><p className="text-xs text-slate-400 mt-2">Completed on</p><p className="font-bold">{date}</p></div>
      <div className="w-24 h-24 rounded-full bg-[#FFD166] text-[#0A192F] grid place-items-center text-center text-[11px] font-black leading-tight">15 / 15<br />LABS</div>
      <div className="text-right"><p className="text-2xl font-black italic">Rakesh Kumar</p><div className="h-px bg-white/40 my-1" /><p className="text-xs text-slate-300">Director – RoboSpark PrP Pvt Ltd, Noida</p></div>
    </div>
  </div>
));
Cert.displayName = "Cert";

const Scaled = ({ children }) => {
  const box = useRef(null);
  const [s, setS] = useState(0.5);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setS(e.contentRect.width / W));
    ro.observe(box.current);
    return () => ro.disconnect();
  }, []);
  return <div ref={box} className="w-full rounded-2xl overflow-hidden shadow-2xl" style={{ height: H * s }} data-testid="cert-preview"><div style={{ transform: `scale(${s})`, transformOrigin: "top left", width: W }}>{children}</div></div>;
};

export default function LabCertificate() {
  const p = useMemo(progress, []);
  const [name, setName] = useState(getCertName());
  const [busy, setBusy] = useState(false);
  const pdfRef = useRef(null);
  const date = p.lastLab ? new Date(p.lastLab).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "—";
  const id = certId(name || "student", p.lastLab || "");
  const ready = p.allLabs && name.trim().length >= 2;
  const props = { name: name.trim(), id, date, labs: p.labs };

  const download = async () => {
    setBusy(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      await html2pdf().set({ margin: 0, filename: `RoboSpark-Robot-Lab-Certificate-${name.trim().replace(/\s+/g, "-")}.pdf`, image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, backgroundColor: "#0A192F" }, jsPDF: { unit: "px", format: [W, H], orientation: "landscape", hotfixes: ["px_scaling"] } }).from(pdfRef.current).save();
    } catch { toast.error("Could not create the certificate. Please try again."); } finally { setBusy(false); }
  };
  const share = async () => {
    const text = `${name.trim()} completed all 15 RoboSpark Robot Lab projects! Certificate ${id}. Try the labs free:`;
    const url = `${window.location.origin}/robot-lab`;
    if (navigator.share) { try { await navigator.share({ title: "RoboSpark Robot Lab Certificate", text, url }); } catch { /* cancelled */ } return; }
    window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, "_blank", "noopener");
  };

  if (!p.allLabs) return (
    <div className="bg-[#F4F7FC] min-h-screen grid place-items-center px-5 py-16" data-testid="cert-locked">
      <div className="max-w-md text-center rounded-3xl bg-white border border-slate-200 p-8">
        <Lock className="w-10 h-10 mx-auto text-slate-400" />
        <h1 className="mt-3 font-['Outfit'] font-black text-3xl text-[#0A192F]">Certificate locked</h1>
        <p className="mt-2 text-slate-600" data-testid="cert-locked-count">{`You've finished ${p.labsDone} of ${p.labs.length} Robot Lab demos. Complete all 15 to unlock your shareable certificate.`}</p>
        <div className="mt-5 flex justify-center gap-2"><Link to="/my-progress" className="rounded-full bg-[#0A192F] text-white px-5 py-2.5 font-bold" data-testid="cert-progress-link">My progress</Link><Link to="/robot-lab" className="rounded-full bg-[#FF2E63] text-white px-5 py-2.5 font-bold" data-testid="cert-labs-link">Continue labs</Link></div>
      </div>
    </div>
  );

  return (
    <div className="bg-[#F4F7FC] min-h-screen" data-testid="cert-page">
      <div className="max-w-5xl mx-auto px-5 py-10 space-y-6">
        <Link to="/my-progress" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-[#0A192F]" data-testid="cert-back-link"><ArrowLeft className="w-4 h-4" />My progress</Link>
        <h1 className="font-['Outfit'] font-black text-4xl sm:text-5xl text-[#0A192F] flex items-center gap-3"><Award className="w-10 h-10 text-[#FF2E63]" />Your Robot Lab Certificate</h1>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex-1 min-w-[220px]"><span className="text-sm font-bold text-slate-600">Name on certificate</span>
            <input value={name} maxLength={40} onChange={(e) => { setName(e.target.value); setCertName(e.target.value); }} placeholder="Student's full name"
              className="mt-1 w-full rounded-xl border border-slate-300 px-4 py-3 text-lg font-semibold focus:outline-none focus:border-[#007A8A]" data-testid="cert-name-input" /></label>
          <button onClick={download} disabled={!ready || busy} className="inline-flex items-center gap-2 rounded-full bg-[#0A192F] text-white px-5 py-3 font-bold disabled:opacity-50" data-testid="cert-download-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Download PDF</button>
          <button onClick={share} disabled={!ready} className="inline-flex items-center gap-2 rounded-full bg-[#3BB273] text-white px-5 py-3 font-bold disabled:opacity-50" data-testid="cert-share-btn"><Share2 className="w-4 h-4" />Share</button>
        </div>
        <Scaled><Cert {...props} /></Scaled>
        <div className="fixed -left-[20000px] top-0" aria-hidden><Cert ref={pdfRef} {...props} /></div>
      </div>
    </div>
  );
}
